import { useMemo, useState } from "react";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { Topbar } from "../../components/AppShell";
import { LoadingPanel } from "../../components/LoadingPanel";
import { SectionCardHeader } from "../../components/ChartCard";
import {
  buildCompanyDailySql, buildCompanySql, buildFeatureDailySql, buildOverviewSql,
  buildUserSql, mapCompanyDailyRows, mapCompanyRows, mapFeatureDailyRows,
  mapOverviewRows, mapUserRows,
} from "./sql";
import type {
  CompanyDailyRow, CompanyRow, FeatureDailyRow, OverviewRow, UserRow,
} from "./sql";
import {
  EMPTY_SET, FiltersBar, defaultFilters, filtersToPills, overviewMatches,
} from "./filters";
import type { Filters } from "./filters";
import {
  HBarChart, KpiCard, MultiLineTimeseries, StackedTimeseriesBars,
  colorFor, formatters,
} from "./charts";
import { BUCKET_PG, bucketLabel, fmtCount, fmtMs, fmtTokens, fmtUsd, fmtUsdCompact } from "./helpers";

export function LlmUsage() {
  const today = useMemo(() => new Date(), []);
  const [filters, setFilters] = useState<Filters>(() => defaultFilters(today));

  const sqlInputs = useMemo(
    () => ({
      start: filters.start,
      end: filters.end,
      companyExternalId: filters.companyExternalId,
      grain: BUCKET_PG[filters.bucket],
    }),
    [filters.start, filters.end, filters.companyExternalId, filters.bucket],
  );

  const overviewSql     = useMemo(() => buildOverviewSql(sqlInputs),     [sqlInputs]);
  const featureDailySql = useMemo(() => buildFeatureDailySql(sqlInputs), [sqlInputs]);
  const companySql      = useMemo(() => buildCompanySql(sqlInputs),      [sqlInputs]);
  const userSql         = useMemo(() => buildUserSql(sqlInputs),         [sqlInputs]);
  const companyDailySql = useMemo(
    () => buildCompanyDailySql({ start: sqlInputs.start, end: sqlInputs.end, grain: sqlInputs.grain }),
    [sqlInputs.start, sqlInputs.end, sqlInputs.grain],
  );

  const overview     = useAdminQuery(overviewSql);
  const featureDaily = useAdminQuery(featureDailySql);
  const company      = useAdminQuery(companySql);
  const user         = useAdminQuery(userSql);
  // Top-10 companies series. Hidden in the UI when a single-company drill-in
  // is active (the chart loses meaning), but still queried so toggling back
  // to the cross-company view is instant.
  const companyDailyEnabled = filters.companyExternalId === null;
  const companyDaily = useAdminQuery(companyDailySql);

  const overviewRows = useMemo<OverviewRow[]>(() => {
    if (!overview.data) return [];
    return mapOverviewRows(overview.data.columns, overview.data.rows as unknown[][]);
  }, [overview.data]);

  const featureDailyRows = useMemo<FeatureDailyRow[]>(() => {
    if (!featureDaily.data) return [];
    return mapFeatureDailyRows(featureDaily.data.columns, featureDaily.data.rows as unknown[][]);
  }, [featureDaily.data]);

  const companyRows = useMemo<CompanyRow[]>(() => {
    if (!company.data) return [];
    return mapCompanyRows(company.data.columns, company.data.rows as unknown[][]);
  }, [company.data]);

  const userRows = useMemo<UserRow[]>(() => {
    if (!user.data) return [];
    return mapUserRows(user.data.columns, user.data.rows as unknown[][]);
  }, [user.data]);

  const companyDailyRows = useMemo<CompanyDailyRow[]>(() => {
    if (!companyDaily.data || !companyDailyEnabled) return [];
    return mapCompanyDailyRows(companyDaily.data.columns, companyDaily.data.rows as unknown[][]);
  }, [companyDaily.data, companyDailyEnabled]);

  // Filter options come from the unfiltered overview so the list doesn't
  // change as the user picks values (which would feel buggy).
  const featureOptions = useMemo(
    () => Array.from(new Set(overviewRows.map((r) => r.name))).sort(),
    [overviewRows],
  );
  const modelOptions = useMemo(
    () => Array.from(new Set(overviewRows.map((r) => r.model))).sort(),
    [overviewRows],
  );
  const companyOptions = useMemo(
    () => companyRows.map((r) => ({ key: r.company_external_id, label: r.company_name })),
    [companyRows],
  );

  // Client-side filtered overview drives KPI tiles, feature toplist /
  // requests bar, and the feature overview table.
  const filteredOverview = useMemo(
    () => overviewRows.filter((r) => overviewMatches(r, filters)),
    [overviewRows, filters],
  );

  const kpis = useMemo(() => {
    const totalCost = filteredOverview.reduce((s, r) => s + r.total_cost, 0);
    const calls     = filteredOverview.reduce((s, r) => s + r.calls, 0);
    const tokens    = filteredOverview.reduce((s, r) => s + r.total_tokens, 0);
    const avgCost   = calls > 0 ? totalCost / calls : 0;
    return { totalCost, calls, tokens, avgCost };
  }, [filteredOverview]);

  // Spend-by-feature toplist: aggregate (name + model) into a single row per
  // feature/model combination, sorted by spend.
  const spendByFeature = useMemo(() => {
    return [...filteredOverview]
      .sort((a, b) => b.total_cost - a.total_cost)
      .slice(0, 15)
      .map((r) => ({
        label: `${r.name} (${r.model})`,
        value: r.total_cost,
      }));
  }, [filteredOverview]);

  // Requests-per-feature: same shape, sorted by call count.
  const requestsByFeature = useMemo(() => {
    return [...filteredOverview]
      .sort((a, b) => b.calls - a.calls)
      .slice(0, 15)
      .map((r) => ({
        label: `${r.name} (${r.model})`,
        value: r.calls,
      }));
  }, [filteredOverview]);

  // Pivot SQL rows (already bucketed at the chosen granularity) into the
  // {label, [feature]: cost} shape Recharts wants. No second-pass bucketing.
  const costOverTime = useMemo(() => {
    const filteredRows = featureDailyRows.filter(
      (r) => filters.features.size === 0 || filters.features.has(r.name),
    );
    if (filteredRows.length === 0) return { data: [], seriesKeys: [] };
    const series = new Set<string>();
    const byBucket = new Map<string, Record<string, number | string>>();
    for (const r of filteredRows) {
      const cell = byBucket.get(r.bucket) ?? { label: bucketLabel(r.bucket, filters.bucket), _iso: r.bucket };
      cell[r.name] = (Number(cell[r.name] ?? 0)) + r.cost;
      byBucket.set(r.bucket, cell);
      series.add(r.name);
    }
    const data = Array.from(byBucket.values())
      .sort((a, b) => String(a._iso).localeCompare(String(b._iso)))
      .map((row) => {
        const out = { ...row };
        for (const k of series) {
          if (out[k] === undefined) out[k] = 0;
        }
        return out;
      });
    return { data, seriesKeys: Array.from(series).sort() };
  }, [featureDailyRows, filters.features, filters.bucket]);

  const latencyOverTime = useMemo(() => {
    const filteredRows = featureDailyRows.filter(
      (r) => filters.features.size === 0 || filters.features.has(r.name),
    );
    if (filteredRows.length === 0) return { data: [], seriesKeys: [] };
    const series = new Set<string>();
    const byBucket = new Map<string, Record<string, number | string>>();
    for (const r of filteredRows) {
      const cell = byBucket.get(r.bucket) ?? { label: bucketLabel(r.bucket, filters.bucket), _iso: r.bucket };
      cell[r.name] = r.p95_latency_ms / 1000;
      byBucket.set(r.bucket, cell);
      series.add(r.name);
    }
    const data = Array.from(byBucket.values())
      .sort((a, b) => String(a._iso).localeCompare(String(b._iso)));
    return { data, seriesKeys: Array.from(series).sort() };
  }, [featureDailyRows, filters.features, filters.bucket]);

  const companyOverTime = useMemo(() => {
    if (!companyDailyEnabled || companyDailyRows.length === 0) {
      return { data: [], seriesKeys: [] };
    }
    const seriesOrder = Array.from(
      new Map(companyDailyRows.map((r) => [r.company_external_id, r.company_name])).values(),
    );
    const byBucket = new Map<string, Record<string, number | string>>();
    for (const r of companyDailyRows) {
      const cell = byBucket.get(r.bucket) ?? { label: bucketLabel(r.bucket, filters.bucket), _iso: r.bucket };
      cell[r.company_name] = (Number(cell[r.company_name] ?? 0)) + r.cost;
      byBucket.set(r.bucket, cell);
    }
    const data = Array.from(byBucket.values())
      .sort((a, b) => String(a._iso).localeCompare(String(b._iso)))
      .map((row) => {
        const out = { ...row };
        for (const k of seriesOrder) {
          if (out[k] === undefined) out[k] = 0;
        }
        return out;
      });
    return { data, seriesKeys: seriesOrder };
  }, [companyDailyRows, companyDailyEnabled, filters.bucket]);

  const isLoading =
    overview.isLoading || featureDaily.isLoading ||
    company.isLoading  || user.isLoading        ||
    (companyDailyEnabled && companyDaily.isLoading);

  const isError =
    overview.isError || featureDaily.isError ||
    company.isError  || user.isError         ||
    (companyDailyEnabled && companyDaily.isError);

  const firstError =
    (overview.error     as Error | undefined) ??
    (featureDaily.error as Error | undefined) ??
    (company.error      as Error | undefined) ??
    (user.error         as Error | undefined) ??
    (companyDaily.error as Error | undefined);

  const pagePills = useMemo(
    () => filtersToPills(filters, {
      companyOptions,
      onClearCompany:  () => setFilters({ ...filters, companyExternalId: null }),
      onClearFeatures: () => setFilters({ ...filters, features: EMPTY_SET }),
      onClearModels:   () => setFilters({ ...filters, models: EMPTY_SET }),
    }),
    [filters, companyOptions],
  );

  return (
    <>
      <Topbar crumbs={["Dashboards", "LLM Usage"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Internal · audit.llm_usage</div>
            <h1>LLM Usage</h1>
            <p className="lede">
              Spend, throughput, and latency for every LLM call routed through the API.
              Pick a date range, slice by feature, model, or company. Data starts 2026-03-26.
            </p>
          </div>
        </div>

        <FiltersBar
          filters={filters}
          onChange={setFilters}
          today={today}
          featureOptions={featureOptions}
          modelOptions={modelOptions}
          companyOptions={companyOptions}
        />

        {isLoading && overviewRows.length === 0 ? (
          <LoadingPanel />
        ) : isError ? (
          <pre style={{ color: "var(--fg-danger)", whiteSpace: "pre-wrap", fontSize: 12 }}>
            {firstError ? (firstError.message || String(firstError)) : "Failed to load data."}
          </pre>
        ) : overviewRows.length === 0 ? (
          <div className="empty">No LLM calls recorded in this window.</div>
        ) : (
          <>
            <KpiRow kpis={kpis} />

            <div className="grid-2" style={{ marginTop: 16 }}>
              <ChartTile
                title="Spend by feature"
                subtitle="Top 15 (feature, model) pairs by total spend in this window."
                pagePills={pagePills}
              >
                <HBarChart
                  data={spendByFeature}
                  color="#2F7A8A"
                  valueFormatter={fmtUsdCompact}
                  valueLabel="Cost (USD)"
                />
              </ChartTile>

              <ChartTile
                title="Requests per feature"
                subtitle="Top 15 (feature, model) pairs by call count in this window."
                pagePills={pagePills}
              >
                <HBarChart
                  data={requestsByFeature}
                  color="#8352C0"
                  valueFormatter={fmtCount}
                  valueLabel="Calls"
                />
              </ChartTile>
            </div>

            <SectionCard
              title="Cost over time"
              subtitle={`Stacked by feature, top-10 features by spend. Bucketed ${filters.bucket}.`}
              pagePills={pagePills}
            >
              <StackedTimeseriesBars
                data={costOverTime.data}
                seriesKeys={costOverTime.seriesKeys}
                formatValue={formatters.cost}
              />
            </SectionCard>

            <SectionCard
              title="Latency p95 by feature (seconds)"
              subtitle={`p95 latency per ${filters.bucket.replace(/ly$/, "")} bucket for the top spending features.`}
              pagePills={pagePills}
            >
              <MultiLineTimeseries
                data={latencyOverTime.data}
                seriesKeys={latencyOverTime.seriesKeys}
                formatValue={(s) => fmtMs(s * 1000)}
              />
            </SectionCard>

            <SectionCard
              title="Feature overview"
              subtitle="Calls, cost, tokens by (feature, model). Sorted by total spend."
              pagePills={pagePills}
            >
              <FeatureTable rows={filteredOverview} />
            </SectionCard>

            <div className="grid-2">
              <ChartTile
                title="Top spend by company"
                subtitle="Top 25 by total spend, with display name resolved from public.companies."
                pagePills={pagePills}
              >
                <CompanyToplist
                  rows={companyRows.slice(0, 25)}
                  onPick={(id) => setFilters({ ...filters, companyExternalId: id })}
                  selected={filters.companyExternalId}
                />
              </ChartTile>

              <ChartTile
                title="Top spend by user"
                subtitle="Top 25 by total spend, with display name resolved from public.users."
                pagePills={pagePills}
              >
                <UserToplist rows={userRows.slice(0, 25)} />
              </ChartTile>
            </div>

            <SectionCard
              title="Company spend breakdown"
              subtitle="Top 50 companies in the window. Click a row to drill in."
              pagePills={pagePills}
            >
              <CompanyTable
                rows={companyRows}
                onPick={(id) => setFilters({ ...filters, companyExternalId: id })}
                selected={filters.companyExternalId}
              />
            </SectionCard>

            <SectionCard
              title="User spend breakdown"
              subtitle="Top 50 users in the window. Joined to companies for context."
              pagePills={pagePills}
            >
              <UserTable rows={userRows} />
            </SectionCard>

            {companyDailyEnabled && (
              <SectionCard
                title="Top 10 companies, spend over time"
                subtitle={`Bucketed ${filters.bucket}. Always uses the unfiltered top-10 companies for the period (does not apply feature/model filters).`}
                pagePills={pagePills}
              >
                <StackedTimeseriesBars
                  data={companyOverTime.data}
                  seriesKeys={companyOverTime.seriesKeys}
                  formatValue={formatters.cost}
                />
              </SectionCard>
            )}
          </>
        )}
      </div>
    </>
  );
}

function KpiRow({ kpis }: { kpis: { totalCost: number; calls: number; tokens: number; avgCost: number } }) {
  return (
    <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
      <KpiCard label="Total spend"      value={fmtUsd(kpis.totalCost)} icon="ti-currency-dollar" />
      <KpiCard label="Total requests"   value={fmtCount(kpis.calls)}   icon="ti-bolt" />
      <KpiCard label="Total tokens"     value={fmtTokens(kpis.tokens)} icon="ti-letter-t" />
      <KpiCard label="Avg cost / request" value={fmtUsd(kpis.avgCost)} sub="Across selected features and models" icon="ti-calculator" />
    </div>
  );
}

function ChartTile({
  title, subtitle, pagePills, children,
}: {
  title: string;
  subtitle: string;
  pagePills?: ReturnType<typeof filtersToPills>;
  children: React.ReactNode;
}) {
  return (
    <div className="chart-tile">
      <SectionCardHeader title={title} subtitle={subtitle} filterPills={pagePills} noBorder />
      {children}
    </div>
  );
}

function SectionCard({
  title, subtitle, pagePills, children,
}: {
  title: string;
  subtitle: string;
  pagePills?: ReturnType<typeof filtersToPills>;
  children: React.ReactNode;
}) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <SectionCardHeader title={title} subtitle={subtitle} filterPills={pagePills} />
      <div className="card-body">{children}</div>
    </div>
  );
}

function FeatureTable({ rows }: { rows: OverviewRow[] }) {
  if (rows.length === 0) return <div className="empty">No data in range.</div>;
  return (
    <div className="table-wrap">
      <table className="t">
        <thead>
          <tr>
            <th>Feature</th>
            <th>Model</th>
            <th className="num">Calls</th>
            <th className="num">Total cost</th>
            <th className="num">Avg cost</th>
            <th className="num">Input</th>
            <th className="num">Cached input</th>
            <th className="num">Output</th>
            <th className="num">Reasoning</th>
            <th className="num">Total tokens</th>
            <th className="num">p95 latency</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.name}-${r.model}`}>
              <td style={{ fontWeight: 600 }}>{r.name}</td>
              <td>{r.model}</td>
              <td className="num">{fmtCount(r.calls)}</td>
              <td className="num mono" style={{ fontWeight: 700 }}>{fmtUsd(r.total_cost)}</td>
              <td className="num mono">{fmtUsd(r.avg_cost)}</td>
              <td className="num">{fmtTokens(r.input_tokens)}</td>
              <td className="num">{fmtTokens(r.cached_input_tokens)}</td>
              <td className="num">{fmtTokens(r.output_tokens)}</td>
              <td className="num">{fmtTokens(r.reasoning_tokens)}</td>
              <td className="num">{fmtTokens(r.total_tokens)}</td>
              <td className="num">{fmtMs(r.p95_latency_ms)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompanyToplist({
  rows, onPick, selected,
}: {
  rows: CompanyRow[];
  onPick: (id: string) => void;
  selected: string | null;
}) {
  if (rows.length === 0) return <div className="empty">No data in range.</div>;
  const max = rows[0]?.total_cost ?? 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {rows.map((r, i) => {
        const pct = (r.total_cost / max) * 100;
        const isSelected = selected === r.company_external_id;
        return (
          <button
            key={r.company_external_id}
            type="button"
            onClick={() => onPick(r.company_external_id)}
            className="llm-toplist-row"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 100px",
              alignItems: "center",
              gap: 10,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: "4px 6px",
              borderRadius: 4,
              textAlign: "left",
              fontWeight: isSelected ? 700 : 500,
              color: isSelected ? "var(--color-teal)" : "var(--fg-1)",
            }}
          >
            <div style={{ position: "relative", paddingRight: 8 }}>
              <div
                style={{
                  position: "absolute",
                  left: 0, top: 4, bottom: 4,
                  width: `${pct}%`,
                  background: colorFor(i),
                  opacity: 0.15,
                  borderRadius: 3,
                }}
              />
              <span style={{ position: "relative", fontSize: 12 }}>
                {r.company_name}
              </span>
            </div>
            <span
              className="num mono"
              style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}
            >
              {fmtUsd(r.total_cost)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function UserToplist({ rows }: { rows: UserRow[] }) {
  if (rows.length === 0) return <div className="empty">No data in range.</div>;
  const max = rows[0]?.total_cost ?? 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {rows.map((r, i) => {
        const pct = (r.total_cost / max) * 100;
        return (
          <div
            key={`${r.user_external_id}-${r.company_external_id ?? "none"}`}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 100px",
              alignItems: "center",
              gap: 10,
              padding: "4px 6px",
            }}
          >
            <div style={{ position: "relative", paddingRight: 8 }}>
              <div
                style={{
                  position: "absolute",
                  left: 0, top: 4, bottom: 4,
                  width: `${pct}%`,
                  background: colorFor(i),
                  opacity: 0.15,
                  borderRadius: 3,
                }}
              />
              <span style={{ position: "relative", fontSize: 12 }}>
                <span style={{ fontWeight: 600 }}>{r.user_name}</span>{" "}
                <span style={{ color: "var(--fg-3)" }}>
                  · {r.company_name}
                </span>
              </span>
            </div>
            <span
              className="num mono"
              style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}
            >
              {fmtUsd(r.total_cost)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function CompanyTable({
  rows, onPick, selected,
}: {
  rows: CompanyRow[];
  onPick: (id: string) => void;
  selected: string | null;
}) {
  if (rows.length === 0) return <div className="empty">No data in range.</div>;
  return (
    <div className="table-wrap">
      <table className="t">
        <thead>
          <tr>
            <th>Company</th>
            <th className="num">Calls</th>
            <th className="num">Total cost</th>
            <th className="num">Avg cost</th>
            <th className="num">Total tokens</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isSelected = selected === r.company_external_id;
            return (
              <tr
                key={r.company_external_id}
                onClick={() => onPick(r.company_external_id)}
                style={{
                  cursor: "pointer",
                  background: isSelected ? "var(--bg-3)" : undefined,
                  fontWeight: isSelected ? 700 : undefined,
                }}
              >
                <td>{r.company_name}</td>
                <td className="num">{fmtCount(r.calls)}</td>
                <td className="num mono" style={{ fontWeight: 700 }}>{fmtUsd(r.total_cost)}</td>
                <td className="num mono">{fmtUsd(r.avg_cost)}</td>
                <td className="num">{fmtTokens(r.total_tokens)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UserTable({ rows }: { rows: UserRow[] }) {
  if (rows.length === 0) return <div className="empty">No data in range.</div>;
  return (
    <div className="table-wrap">
      <table className="t">
        <thead>
          <tr>
            <th>User</th>
            <th>Email</th>
            <th>Company</th>
            <th className="num">Calls</th>
            <th className="num">Total cost</th>
            <th className="num">Avg cost</th>
            <th className="num">Total tokens</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.user_external_id}-${r.company_external_id ?? "none"}`}>
              <td style={{ fontWeight: 600 }}>{r.user_name}</td>
              <td style={{ color: "var(--fg-3)" }}>{r.user_email}</td>
              <td>{r.company_name}</td>
              <td className="num">{fmtCount(r.calls)}</td>
              <td className="num mono" style={{ fontWeight: 700 }}>{fmtUsd(r.total_cost)}</td>
              <td className="num mono">{fmtUsd(r.avg_cost)}</td>
              <td className="num">{fmtTokens(r.total_tokens)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
